import { Button } from '@codegouvfr/react-dsfr/Button';
import { useTextes } from '@common/config';
import { OrganismeFichierType, useDownloadOrganismeFichierMutation } from '@common/services';
import { FileUploadItem, SavedFile, viewFile } from '@common/ui';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInscriptionState } from '../../hooks/use-inscription-state.hook';
import { useSummaryActions } from './hooks/use-summary-actions.hook';

export const SummaryPage = () => {
  const t = useTextes('Inscription', 'Récapitulatif');
  const navigate = useNavigate();
  const inscription = useInscriptionState();

  const [organismeAlreadyExistError, setOrganismeAlreadyExistError] = useState<string | null>(null);

  const { sendInscriptionData, isLoading } = useSummaryActions();

  const [downloadOrganismeFichier] = useDownloadOrganismeFichierMutation();

  const handleValidate = async () => {
    try {
      await sendInscriptionData();
      navigate('/demande-envoyee');
    } catch (err) {
      console.error(err);
      if (err.status === 409) setOrganismeAlreadyExistError(err.data.error);
    }
  };

  const handleView = (file: SavedFile, fichierType: OrganismeFichierType) => {
    if (file.url) viewFile(file.url);
    else downloadOrganismeFichier({ id: inscription.organisme!.id, fichierType });
  };

  return (
    <div className={'flex flex-col gap-6'}>

      <h3>Récapitulatif</h3>

      <div className={'separator'} />

      <div className={'p-3 border border-2 border-gray-200 rounded-md'}>
        <div className={'flex justify-between'}>
          <h4 className={'color-blue-primary'}>Administrateur 1</h4>
          <Button
            onClick={() => navigate('/inscription/etape-1')}
            type={'button'}
            priority={'secondary'}
            className={'h-fit'}>MODIFIER</Button>
        </div>
        <div className={'mt-4 grid grid-cols-3 gap-4'}>
          <div className={'flex flex-col'}>
            <span className={'color-blue-primary'}>Nom</span>
            <span>{inscription.admin1?.nom}</span>
          </div>
          <div className={'flex flex-col'}>
            <span className={'color-blue-primary'}>Prénom</span>
            <span>{inscription.admin1?.prenom}</span>
          </div>
          <div className={'flex flex-col'}>
            <span className={'color-blue-primary'}>Téléphone</span>
            <span>{inscription.admin1?.telephone}</span>
          </div>
          <div className={'flex flex-col'}>
            <span className={'color-blue-primary'}>Courriel</span>
            <span>{inscription.admin1?.email}</span>
          </div>
        </div>
      </div>

      {(!!inscription.admin2) && (
        <div className={'p-3 border border-2 border-gray-200 rounded-md'}>
          <div className={'flex justify-between'}>
            <h4 className={'color-blue-primary'}>Administrateur 2</h4>
            <Button
              onClick={() => navigate('/inscription/etape-1')}
              type={'button'}
              priority={'secondary'}
              className={'h-fit'}>MODIFIER</Button>
          </div>
          <div className={'mt-4 grid grid-cols-3 gap-4'}>
            <div className={'flex flex-col'}>
              <span className={'color-blue-primary'}>Nom</span>
              <span>{inscription.admin2.nom}</span>
            </div>
            <div className={'flex flex-col'}>
              <span className={'color-blue-primary'}>Prénom</span>
              <span>{inscription.admin2.prenom}</span>
            </div>
            <div className={'flex flex-col'}>
              <span className={'color-blue-primary'}>Téléphone</span>
              <span>{inscription.admin2.telephone}</span>
            </div>
            <div className={'flex flex-col'}>
              <span className={'color-blue-primary'}>Courriel</span>
              <span>{inscription.admin2.email}</span>
            </div>
          </div>
        </div>
      )}

      <div className={'p-3 border border-2 border-gray-200 rounded-md'}>
        <div className={'flex justify-between'}>
          <h4 className={'color-blue-primary'}>Représentant légal</h4>
          <Button
            onClick={() => navigate('/inscription/etape-2')}
            type={'button'}
            priority={'secondary'}
            className={'h-fit'}>MODIFIER</Button>
        </div>
        <div className={'mt-4 grid grid-cols-3 gap-4'}>
          <div className={'flex flex-col'}>
            <span className={'color-blue-primary'}>Nom</span>
            <span>{inscription.representant?.nom}</span>
          </div>
          <div className={'flex flex-col'}>
            <span className={'color-blue-primary'}>Prénom</span>
            <span>{inscription.representant?.prenom}</span>
          </div>
          <div className={'flex flex-col'}>
            <span className={'color-blue-primary'}>Téléphone</span>
            <span>{inscription.representant?.telephone}</span>
          </div>
          <div className={'flex flex-col'}>
            <span className={'color-blue-primary'}>Courriel</span>
            <span>{inscription.representant?.email}</span>
          </div>
        </div>

        <div className={'flex flex-col mt-4 gap-2'}>
          <span className={'color-blue-primary'}>Attestation du représentant légal</span>
          {(!!inscription.attestation) && (
            <FileUploadItem file={inscription.attestation}
                            handleView={() => handleView(inscription.attestation!, OrganismeFichierType.ATTESTATION_REPRESENTANT)} />
          )}
        </div>
      </div>

      {(!!inscription.delegataire) && (
        <div className={'p-3 border border-2 border-gray-200 rounded-md'}>
          <div className={'flex justify-between'}>
            <h4 className={'color-blue-primary'}>Délégataire</h4>
            <Button
              onClick={() => navigate('/inscription/etape-2')}
              type={'button'}
              priority={'secondary'}
              className={'h-fit'}>MODIFIER</Button>
          </div>
          <div className={'mt-4 grid grid-cols-3 gap-4'}>
            <div className={'flex flex-col'}>
              <span className={'color-blue-primary'}>Nom</span>
              <span>{inscription.delegataire.nom}</span>
            </div>
            <div className={'flex flex-col'}>
              <span className={'color-blue-primary'}>Prénom</span>
              <span>{inscription.delegataire.prenom}</span>
            </div>
            <div className={'flex flex-col'}>
              <span className={'color-blue-primary'}>Courriel</span>
              <span>{inscription.delegataire.email}</span>
            </div>
          </div>
          <div className={'flex flex-col mt-4 gap-2'}>
            <span className={'color-blue-primary'}>Attestation de délégation</span>
            {(!!inscription.delegation) && (
              <FileUploadItem file={inscription.delegation}
                              handleView={() => handleView(inscription.delegation!, OrganismeFichierType.DELEGATION_POUVOIR)} />
            )}
          </div>
        </div>
      )}

      <div className={'p-3 border border-2 border-gray-200 rounded-md'}>
        <div className={'flex justify-between'}>
          <h4 className={'color-blue-primary'}>Document et informations complémentaires</h4>
          <Button
            onClick={() => navigate('/inscription/etape-3')}
            type={'button'}
            priority={'secondary'}
            className={'h-fit'}>MODIFIER</Button>
        </div>
        <div className={'mt-4 grid grid-cols-3 gap-4'}>
          <div className={'flex flex-col'}>
            <span className={'color-blue-primary'}>Effectif d'apprentis</span>
            <span>{inscription.organisme?.effectifApprenti}</span>
          </div>
          <div className={'flex flex-col'}>
            <span className={'color-blue-primary'}>NDA</span>
            <span>{inscription.organisme?.nda || t['COMP_MES4']}</span>
          </div>
        </div>
      </div>

      {(organismeAlreadyExistError) && (
        <div className={'flex text-red-500 mb-4 justify-end'}>
          <span>{organismeAlreadyExistError}</span>
        </div>
      )}

      <div className={'flex gap-3 justify-end'}>
        <Button priority={'secondary'} onClick={() => navigate('/inscription/etape-3')}>PRÉCÉDENT</Button>
        <Button disabled={organismeAlreadyExistError || isLoading} priority={'primary'} onClick={handleValidate}>ENVOYER
          LA DEMANDE</Button>
      </div>
    </div>
  );
};